/**
 * Resolve entity merge tags in markdown content.
 *
 * Merge tag format:
 *   {{slide:SLIDE_ID}}              → slide display name (case label or file name)
 *   {{slide.link:SLIDE_ID}}         → markdown link [displayName](viewerUrl)
 *   {{marker:MARKER_ID}}            → marker display name
 *   {{cellType:CELL_TYPE_ID}}       → cell type display name
 *
 * This is a pure string transform — call it after building the lookups
 * from prefetched slide data, search index, and taxonomy data.
 */

import { SlidePrefetchData } from 'interfaces/slide';

const ENTITY_MERGE_TAG_PATTERN =
  /\{\{\s*(slide|marker|cellType)(?:\.(link|displayName|color))?\s*:\s*([^}\s]+)\s*\}\}/g;

export interface EntityMergeTagLookups {
  slideById: Record<string, SlidePrefetchData | { slideId: string; caseLabel?: string; caseId?: number }>;
  markerLabelById: Record<string, string>;
  cellTypeLabelById: Record<string, string>;
}

export interface EntityMergeTagContext {
  studyId?: string;
  labId?: string;
}

const buildSlideViewerUrl = ({ slideId, studyId, labId }: { slideId: string; studyId?: string; labId?: string }) => {
  const basePath = studyId ? `/#/study/${studyId}/slides/${slideId}` : `/#/slides/${slideId}`;
  return labId ? `${basePath}?labId=${labId}` : basePath;
};

const getSlideDisplayName = (
  slide?: SlidePrefetchData | { slideId: string; caseLabel?: string; caseId?: number }
): string | undefined => {
  if (!slide) {
    return undefined;
  }

  if (slide.caseLabel) {
    return slide.caseLabel;
  }

  const slidePrefetch = slide as SlidePrefetchData;
  if (slidePrefetch.originalFileName) {
    return slidePrefetch.originalFileName;
  }

  return undefined;
};

export const collectEntityIdsFromMergeTags = (
  content: string
): { slideIds: string[]; markerIds: string[]; cellTypeIds: string[] } => {
  const slideIds: string[] = [];
  const markerIds: string[] = [];
  const cellTypeIds: string[] = [];

  const pattern = new RegExp(ENTITY_MERGE_TAG_PATTERN);
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(content))) {
    const entityType = match[1] as 'slide' | 'marker' | 'cellType';
    const entityId = match[3];

    if (entityType === 'slide') {
      slideIds.push(entityId);
    } else if (entityType === 'marker') {
      markerIds.push(entityId);
    } else if (entityType === 'cellType') {
      cellTypeIds.push(entityId);
    }
  }

  return { slideIds, markerIds, cellTypeIds };
};

export const resolveEntityMergeTags = (
  content: string,
  lookups: EntityMergeTagLookups,
  context: EntityMergeTagContext
): string => {
  return content.replace(
    ENTITY_MERGE_TAG_PATTERN,
    (_match, entityType: string, attribute: string | undefined, entityId: string) => {
      if (entityType === 'slide') {
        const slide = lookups.slideById[entityId];
        const displayName = getSlideDisplayName(slide) || entityId;

        if (attribute === 'link') {
          const viewerUrl = buildSlideViewerUrl({
            slideId: entityId,
            studyId: context.studyId,
            labId: context.labId,
          });
          const labIdAttr = context.labId ? ` labid="${context.labId}"` : '';
          return `<slide-link slideid="${entityId}" href="${viewerUrl}"${labIdAttr}>${displayName}</slide-link>`;
        }

        return displayName;
      }

      if (entityType === 'marker') {
        return lookups.markerLabelById[entityId] || entityId;
      }

      if (entityType === 'cellType') {
        return lookups.cellTypeLabelById[entityId] || entityId;
      }

      return entityId;
    }
  );
};
